/**
 * Google Ads selection flow (C-fix3): account → campaigns → recap. Reusable,
 * mounted in the onboarding step 3 and the "Manage campaigns" page. Wired to the
 * real endpoints (account-tree / campaigns / watched) — no localStorage, no fake
 * data. MCC managers are group headers (NOT selectable); the client picks a
 * client account then its campaigns. All amounts are in the ACCOUNT currency.
 */
import { useTranslation } from '@silospot/i18n/react';
import type { GoogleAdsAccountNode, GoogleAdsCampaign } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronRight, Loader2, Megaphone, Search } from 'lucide-react';
import { type ReactElement, type ReactNode, useEffect, useMemo, useState } from 'react';
import { api, serverError } from '../../lib/api';
import { Badge, Btn, Chip, Hint, Toggle } from './kit';

type Phase = 'accounts' | 'error' | 'campaigns' | 'recap' | 'success';

interface PickedAccount {
  id: string;
  name: string;
  currencyCode: string;
  timeZone: string;
}

export interface GadsFlowInitial {
  customerId: string;
  name: string;
  campaignIds: string[];
  periodDays: 30 | 90;
  autoSync: boolean;
}

const CHANNEL_LABELS: Record<string, string> = {
  SEARCH: 'Search',
  PERFORMANCE_MAX: 'Performance Max',
  DISPLAY: 'Display',
  SHOPPING: 'Shopping',
  VIDEO: 'Vidéo',
  DEMAND_GEN: 'Demand Gen',
  DISCOVERY: 'Discovery',
  LOCAL: 'Local',
  LOCAL_SERVICES: 'Local Services',
  SMART: 'Smart',
  HOTEL: 'Hotel',
  MULTI_CHANNEL: 'Multi-canal',
  TRAVEL: 'Travel',
};
export function channelLabel(t: string): string {
  if (CHANNEL_LABELS[t]) return CHANNEL_LABELS[t];
  if (!t) return '—';
  return t.charAt(0) + t.slice(1).toLowerCase().replace(/_/g, ' ');
}

export function money(micros: number, currency: string, lang: string): string {
  try {
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 0,
    }).format(micros / 1_000_000);
  } catch {
    return `${Math.round(micros / 1_000_000)} ${currency || ''}`.trim();
  }
}
export const num = (n: number, lang: string): string => new Intl.NumberFormat(lang).format(n);

export function StatusBadge({ status }: { status: GoogleAdsCampaign['status'] }): ReactElement {
  const { t } = useTranslation('pages');
  if (status === 'active')
    return (
      <Badge tone="clean" dot>
        {t('gadsf.stActive')}
      </Badge>
    );
  if (status === 'paused') return <Badge tone="suspect">{t('gadsf.stPaused')}</Badge>;
  if (status === 'removed') return <Badge tone="neutral">{t('gadsf.stRemoved')}</Badge>;
  return <Badge tone="neutral">{t('gadsf.stUnknown')}</Badge>;
}

function Centered({ children }: { children: ReactNode }): ReactElement {
  return <div className="grid place-items-center gap-3 py-12 text-center">{children}</div>;
}

/**
 * Footer for every blocking Google Ads state (no account / access denied / load
 * error / empty account). Never a dead-end: a clear recovery first (retry +
 * "choose another account" when several are reachable), then the framed
 * "connect later" escape (onboarding only) — honest that ad-fraud detection
 * stays pending until Google Ads is connected — and a Support link.
 */
function BlockedActions({
  onRetry,
  onChooseAnother,
  onSkip,
}: {
  onRetry?: () => void;
  onChooseAnother?: () => void;
  onSkip?: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');
  return (
    <div className="flex w-full max-w-md flex-col items-center gap-3">
      {onRetry || onChooseAnother ? (
        <div className="flex flex-wrap justify-center gap-2">
          {onRetry ? (
            <Btn primary onClick={onRetry}>
              {t('gadsf.retry')}
            </Btn>
          ) : null}
          {onChooseAnother ? (
            <Btn onClick={onChooseAnother}>← {t('gadsf.chooseAnother')}</Btn>
          ) : null}
        </div>
      ) : null}
      {onSkip ? (
        <div className="mt-1 w-full rounded-xl border border-border bg-bg/40 px-3.5 py-3 text-center">
          <Btn onClick={onSkip}>{t('onb.adsLater')}</Btn>
          <p className="mx-auto mt-2 max-w-sm text-[11.5px] leading-relaxed text-text-3">
            {t('onb.adsLaterSub')}
          </p>
        </div>
      ) : null}
      <a
        href="/support"
        target="_blank"
        rel="noreferrer"
        className="text-[12px] font-semibold text-primary hover:underline"
      >
        {t('onb.support')}
      </a>
    </div>
  );
}

export function GadsFlow({
  projectId,
  projectName,
  initial,
  onDone,
  onBack,
  onSkip,
}: {
  projectId: string;
  projectName: string;
  initial?: GadsFlowInitial | null;
  onDone?: (r: { customerId: string; name: string; count: number }) => void;
  onBack?: () => void;
  /** Anti-dead-end (onboarding): continue without Google Ads when there is no
   *  account / no campaign / a connection error, so the client is never trapped. */
  onSkip?: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');

  const [phase, setPhase] = useState<Phase>('accounts');
  const [account, setAccount] = useState<PickedAccount | null>(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [sel, setSel] = useState<Set<string>>(new Set(initial?.campaignIds ?? []));
  const [period, setPeriod] = useState<30 | 90>(initial?.periodDays ?? 90);
  const [autoSync, setAutoSync] = useState(initial?.autoSync ?? true);
  const [autoSelected, setAutoSelected] = useState(false);

  const treeQ = useQuery({
    queryKey: ['gads-tree', projectId],
    queryFn: () => api.googleAdsAccountTree(projectId),
    enabled: phase === 'accounts',
  });
  const campsQ = useQuery({
    queryKey: ['gads-camps', projectId, account?.id, period],
    queryFn: () => api.googleAdsSiteCampaigns(projectId, account?.id ?? '', period),
    enabled: !!account && (phase === 'campaigns' || phase === 'recap'),
  });

  // Default to ALL non-removed campaigns checked when the list first loads (the
  // client can uncheck, but must keep ≥1). Editing an existing selection (initial)
  // keeps the stored choice. Re-defaults once per account (see pickAccount).
  useEffect(() => {
    if (initial || autoSelected) return;
    const list = campsQ.data?.campaigns;
    if (!list?.length) return;
    setSel(new Set(list.filter((c) => c.status !== 'removed').map((c) => c.id)));
    setAutoSelected(true);
  }, [campsQ.data, initial, autoSelected]);

  const save = useMutation({
    mutationFn: () =>
      api.googleAdsSetWatched(projectId, {
        customerId: account?.id ?? '',
        campaignIds: [...sel],
        periodDays: period,
        autoSync,
      }),
    onSuccess: () => {
      setPhase('success');
      if (account) onDone?.({ customerId: account.id, name: account.name, count: sel.size });
    },
    onError: (e) => toast.error(serverError(e) ?? t('gads.errGeneric')),
  });

  // Re-launch the Google OAuth consent when the stored token expired/was revoked
  // (returns to the current page so the wizard resumes at the Ads step).
  const reconnect = useMutation({
    mutationFn: () => api.googleAdsConnect(window.location.pathname),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) => toast.error(serverError(e) ?? t('gads.errGeneric')),
  });

  const pickAccount = (a: GoogleAdsAccountNode) => {
    if (a.accessError) {
      setAccount({
        id: a.id,
        name: a.name || a.id,
        currencyCode: a.currencyCode,
        timeZone: a.timeZone,
      });
      setPhase('error');
      return;
    }
    setAccount({
      id: a.id,
      name: a.name || a.id,
      currencyCode: a.currencyCode,
      timeZone: a.timeZone,
    });
    if (!initial) {
      setSel(new Set());
      setAutoSelected(false);
    }
    setPhase('campaigns');
  };

  const camps = campsQ.data?.campaigns ?? [];
  // Is there another client account to fall back to? (treeQ stays cached after
  // leaving the accounts phase.) Drives "choose another account" in dead-ends.
  const multipleAccounts = (treeQ.data?.accounts ?? []).filter((a) => a.type !== 'mcc').length > 1;
  const channelsPresent = useMemo(
    () => [...new Set(camps.map((c) => c.channelType))].filter(Boolean),
    [camps],
  );
  const shown = camps.filter((c) => {
    if (filter === 'active') return c.status === 'active';
    if (filter === 'paused') return c.status === 'paused';
    if (filter.startsWith('ch:')) return c.channelType === filter.slice(3);
    return true;
  });
  const selectable = camps.filter((c) => c.status !== 'removed');
  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  // --- Accounts (tree) -------------------------------------------------------
  if (phase === 'accounts') {
    if (treeQ.isLoading) {
      return (
        <Centered>
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <div className="text-[13px] text-text-3">{t('gadsf.loadingAccounts')}</div>
        </Centered>
      );
    }
    // Token expired/revoked (e.g. a Google OAuth app in "Testing" status, where
    // refresh tokens last 7 days) → prompt a reconnect. This is NOT "no account".
    if (treeQ.data?.reauthRequired) {
      return (
        <Centered>
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-suspect-bg text-suspect-fg">
            <AlertTriangle className="h-6 w-6" />
          </span>
          <div className="text-[15px] font-bold text-text">{t('gadsf.reauthTitle')}</div>
          <div className="max-w-md text-[13px] text-text-3">{t('gadsf.reauthBody')}</div>
          <div className="flex w-full max-w-md flex-col items-center gap-3">
            <Btn primary disabled={reconnect.isPending} onClick={() => reconnect.mutate()}>
              {reconnect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('gadsf.reconnect')}
            </Btn>
            <a
              href="/support"
              target="_blank"
              rel="noreferrer"
              className="text-[12px] font-semibold text-primary hover:underline"
            >
              {t('onb.support')}
            </a>
          </div>
        </Centered>
      );
    }
    // A genuine load failure (network / 5xx) — distinct from an empty account list.
    if (treeQ.isError) {
      return (
        <Centered>
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-fraud-bg text-fraud-fg">
            <AlertTriangle className="h-6 w-6" />
          </span>
          <div className="text-[15px] font-bold text-text">{t('gadsf.loadErrorTitle')}</div>
          <div className="max-w-md text-[13px] text-text-3">{t('gadsf.loadErrorBody')}</div>
          <BlockedActions onRetry={() => void treeQ.refetch()} onSkip={onSkip} />
        </Centered>
      );
    }
    const all = treeQ.data?.accounts ?? [];
    if (all.length === 0) {
      return (
        <Centered>
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-neutral-bg text-text-3">
            <Megaphone className="h-6 w-6" />
          </span>
          <div className="text-[15px] font-bold text-text">{t('gadsf.noAccountTitle')}</div>
          <div className="max-w-md text-[13px] text-text-3">{t('gadsf.noAccountBody')}</div>
          <BlockedActions onRetry={() => void treeQ.refetch()} onSkip={onSkip} />
        </Centered>
      );
    }
    const ql = q.trim().toLowerCase();
    const match = (a: GoogleAdsAccountNode) =>
      !ql || `${a.name} ${a.id}`.toLowerCase().includes(ql);
    const mccs = all.filter((a) => a.type === 'mcc');
    const mccIds = new Set(mccs.map((m) => m.id));
    const solo = all.filter((a) => a.type === 'client' && (!a.parent || !mccIds.has(a.parent)));

    const Row = ({ a, child }: { a: GoogleAdsAccountNode; child?: boolean }) => (
      <button
        type="button"
        onClick={() => pickAccount(a)}
        style={child ? { marginLeft: 22 } : undefined}
        className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2 text-left hover:border-border-strong"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[13.5px] font-semibold text-text">
            {a.name || t('gads.account')}
            {a.accessError ? (
              <Badge tone="fraud" dot>
                {t('gadsf.accessLimited')}
              </Badge>
            ) : null}
          </div>
          <div className="font-mono text-[11.5px] text-text-3">{a.id}</div>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-text-3" />
      </button>
    );

    return (
      <div>
        <div className="text-[14.5px] font-bold text-text">
          {t('gadsf.whichAccount', { site: projectName })}
        </div>
        <p className="mb-3 mt-0.5 text-[12.5px] text-text-3">{t('gadsf.accountsHint')}</p>
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-border-strong bg-surface px-2.5 py-1.5">
          <Search className="h-3.5 w-3.5 text-text-3" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('gads.linkSearchPlaceholder')}
            className="w-full bg-transparent text-[13px] outline-none"
          />
        </div>
        <div className="flex max-h-[320px] flex-col gap-1.5 overflow-y-auto pr-1">
          {mccs.map((m) => {
            const children = m.children
              .map((cid) => all.find((x) => x.id === cid))
              .filter((x): x is GoogleAdsAccountNode => !!x && match(x));
            if (!match(m) && children.length === 0) return null;
            return (
              <div key={m.id} className="flex flex-col gap-1">
                <div className="flex items-center gap-3 rounded-lg bg-bg px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[13.5px] font-semibold text-text">
                      {m.name || t('gads.account')}
                      <Badge tone="bot">{t('gadsf.mcc')}</Badge>
                    </div>
                    <div className="font-mono text-[11.5px] text-text-3">
                      {m.id} · {t('gadsf.clientCount', { count: m.children.length })}
                    </div>
                  </div>
                </div>
                {children.map((c) => (
                  <Row key={c.id} a={c} child />
                ))}
              </div>
            );
          })}
          {solo.filter(match).map((a) => (
            <Row key={a.id} a={a} />
          ))}
          {mccs.every((m) => !match(m)) && solo.filter(match).length === 0 ? (
            <div className="px-1 py-4 text-[12.5px] text-text-3">{t('gads.noMatches')}</div>
          ) : null}
        </div>
        {onBack ? (
          <div className="mt-4">
            <Btn onClick={onBack}>← {t('onb.back')}</Btn>
          </div>
        ) : null}
      </div>
    );
  }

  // --- Access error ----------------------------------------------------------
  if (phase === 'error' && account) {
    return (
      <Centered>
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-fraud-bg text-fraud-fg">
          <AlertTriangle className="h-6 w-6" />
        </span>
        <div className="text-[15px] font-bold text-text">
          {t('gadsf.accessDeniedTitle', { name: account.name })}
        </div>
        <div className="max-w-md text-[13px] text-text-3">
          {t('gadsf.accessDeniedBody', { id: account.id })}
        </div>
        <BlockedActions onChooseAnother={() => setPhase('accounts')} onSkip={onSkip} />
      </Centered>
    );
  }

  // --- Campaigns -------------------------------------------------------------
  if (phase === 'campaigns' && account) {
    if (campsQ.isLoading) {
      return (
        <Centered>
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <div className="text-[13px] text-text-3">
            {t('gadsf.loadingCamps', { name: account.name })}
          </div>
        </Centered>
      );
    }
    // Distinguish a failed request from a genuinely empty account: a query error
    // must NOT read as "no campaign".
    if (campsQ.isError) {
      return (
        <Centered>
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-fraud-bg text-fraud-fg">
            <AlertTriangle className="h-6 w-6" />
          </span>
          <div className="text-[15px] font-bold text-text">{t('gadsf.loadErrorTitle')}</div>
          <div className="max-w-md text-[13px] text-text-3">{t('gadsf.loadErrorBody')}</div>
          <BlockedActions
            onRetry={() => void campsQ.refetch()}
            onChooseAnother={multipleAccounts ? () => setPhase('accounts') : undefined}
            onSkip={onSkip}
          />
        </Centered>
      );
    }
    if (camps.length === 0) {
      return (
        <Centered>
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-suspect-bg text-suspect-fg">
            <Megaphone className="h-6 w-6" />
          </span>
          <div className="text-[15px] font-bold text-text">{t('gadsf.emptyTitle')}</div>
          <div className="max-w-md text-[13px] text-text-3">
            {t('gadsf.emptyBody', { name: account.name, id: account.id })}
          </div>
          <BlockedActions
            onRetry={() => void campsQ.refetch()}
            onChooseAnother={multipleAccounts ? () => setPhase('accounts') : undefined}
            onSkip={onSkip}
          />
        </Centered>
      );
    }
    const filters: Array<{ key: string; label: string }> = [
      { key: 'all', label: t('gadsf.fAll') },
      { key: 'active', label: t('gadsf.fActive') },
      { key: 'paused', label: t('gadsf.fPaused') },
      ...channelsPresent.map((c) => ({ key: `ch:${c}`, label: channelLabel(c) })),
    ];
    return (
      <div>
        <div className="mb-1 flex flex-wrap items-center gap-2.5">
          <div className="text-[14.5px] font-bold text-text">{t('gadsf.campsTitle')}</div>
          <Badge tone="blue">{account.name}</Badge>
          <button
            type="button"
            onClick={() => setPhase('accounts')}
            className="text-[12.5px] font-semibold text-primary hover:underline"
          >
            {t('gadsf.changeAccount')}
          </button>
        </div>
        <p className="mb-2.5 text-[12.5px] text-text-3">{t('gadsf.campsHint')}</p>
        <div className="mb-2.5 flex flex-wrap gap-1.5">
          {filters.map((f) => (
            <Chip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label}
            </Chip>
          ))}
        </div>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="bg-bg text-[11px] uppercase tracking-[0.04em] text-text-3">
                <th className="w-9 px-2 py-2.5">
                  <input
                    type="checkbox"
                    aria-label={t('gadsf.selectAll')}
                    checked={selectable.length > 0 && sel.size === selectable.length}
                    onChange={(e) =>
                      setSel(e.target.checked ? new Set(selectable.map((c) => c.id)) : new Set())
                    }
                  />
                </th>
                <th className="px-2 py-2.5 text-left">{t('gadsf.colCampaign')}</th>
                <th className="px-2 py-2.5 text-left">{t('gadsf.colStatus')}</th>
                <th className="px-2 py-2.5 text-left">{t('gadsf.colType')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => {
                const off = c.status === 'removed';
                return (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: convenience row toggle; the checkbox is the keyboard-accessible control
                  <tr
                    key={c.id}
                    onClick={() => !off && toggle(c.id)}
                    className={`border-t border-[#f1f3f6] ${off ? 'opacity-55' : 'cursor-pointer hover:bg-[#fafbfc]'}`}
                  >
                    <td className="px-2 py-2.5">
                      <input
                        type="checkbox"
                        disabled={off}
                        checked={sel.has(c.id)}
                        onChange={() => toggle(c.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-1.5 font-semibold text-text">
                        {c.name}
                        {c.fresh ? <Badge tone="info">{t('gadsf.new')}</Badge> : null}
                      </div>
                      <div className="font-mono text-[11px] text-text-3">{c.id}</div>
                    </td>
                    <td className="px-2 py-2.5">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-2 py-2.5 text-[12.5px] text-text-2">
                      {channelLabel(c.channelType)}
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-2 py-5 text-center text-[12.5px] text-text-3">
                    {t('gadsf.noFilterMatch')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {/* Help chips on their own row… */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Chip onClick={() => setSel(new Set(selectable.map((c) => c.id)))}>
            {t('gadsf.selectAllBtn')}
          </Chip>
          <Chip
            onClick={() =>
              setSel(new Set(camps.filter((c) => c.status === 'active').map((c) => c.id)))
            }
          >
            {t('gadsf.selectActive')}
          </Chip>
          <Chip onClick={() => setSel(new Set())}>{t('gadsf.clearSel')}</Chip>
        </div>
        {/* …then the counter + "Continue" on its own line, always visible. */}
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
          {sel.size === 0 ? (
            <span className="text-[12px] text-suspect-fg">{t('gadsf.pickAtLeastOne')}</span>
          ) : (
            <span className="text-[12.5px] text-text-2">
              {t('gadsf.nSelected', { count: sel.size })}
            </span>
          )}
          <Btn primary disabled={sel.size === 0} onClick={() => setPhase('recap')}>
            {t('gadsf.continue')} →
          </Btn>
        </div>
      </div>
    );
  }

  // --- Recap -----------------------------------------------------------------
  if (phase === 'recap' && account) {
    const chosen = camps.filter((c) => sel.has(c.id));
    return (
      <div>
        <div className="mb-3 text-[14.5px] font-bold text-text">{t('gadsf.recapTitle')}</div>
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-bg/40 p-4">
          {[
            [t('gadsf.recapProject'), projectName],
            [t('gadsf.recapAccount'), `${account.name} · ${account.id}`],
            [t('gadsf.recapCampaigns'), t('gadsf.nCampaigns', { count: sel.size })],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-[13px]">
              <span className="text-text-3">{k}</span>
              <strong className="text-text">{v}</strong>
            </div>
          ))}
          <div className="flex flex-wrap gap-1.5">
            {chosen.map((c) => (
              <Badge key={c.id} tone="blue">
                {c.name}
              </Badge>
            ))}
          </div>
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-text-3">{t('gadsf.recapPeriod')}</span>
            <div className="inline-flex gap-0.5 rounded-md bg-neutral-bg p-[3px]">
              {([30, 90] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={`rounded-[6px] px-3 py-1 text-[12.5px] font-semibold ${period === p ? 'bg-surface text-text shadow-[0_1px_2px_rgba(16,24,40,0.08)]' : 'text-text-2'}`}
                >
                  {t('gadsf.lastNDays', { n: p })}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between text-[13px]">
            <span className="inline-flex items-center gap-1.5 text-text-3">
              {t('gadsf.recapAutoSync')}
              <Hint text={t('gadsf.autoSyncHint')} />
            </span>
            <span className="inline-flex items-center gap-2">
              <Toggle
                checked={autoSync}
                onChange={setAutoSync}
                aria-label={t('gadsf.recapAutoSync')}
              />
              <span className="text-[12.5px] text-text-2">{t('gadsf.everyFewMin')}</span>
            </span>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <Btn onClick={() => setPhase('campaigns')}>← {t('gadsf.editSelection')}</Btn>
          <Btn primary disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('gadsf.launchImport')}
          </Btn>
        </div>
      </div>
    );
  }

  // --- Success ---------------------------------------------------------------
  return (
    <Centered>
      <span className="grid h-12 w-12 place-items-center rounded-2xl bg-clean-bg text-clean-fg">
        <Check className="h-6 w-6" />
      </span>
      <div className="text-[15px] font-bold text-text">{t('gadsf.successTitle')}</div>
      <div className="max-w-md text-[13px] text-text-3">
        {t('gadsf.successBody', { count: sel.size, name: account?.name ?? '' })}
      </div>
    </Centered>
  );
}
